import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import api, { apiError } from "@/lib/api";
import { ROLE_LABELS, BOOKING_TYPES, isOwnStay, bookingDisplayTitle, taskDisplayTitle } from "@/lib/constants";
import { CommentThread } from "@/components/CommentThread";
import { ReportPanel } from "@/components/ReportPanel";
import { ReportSettingsDialog } from "@/components/ReportSettingsDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { CalendarDays, Plus, Clock, Loader2, Trash2, User, Check, ExternalLink, Settings, Upload, Download, MessageSquare, Megaphone, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Search, X, MessageCircle, Home as HomeIcon, ListChecks, MapPin, Phone, Users, Key, Sparkles, LogOut, Shirt, AlertTriangle, FileText, Info, Moon, UserCog, CalendarPlus } from "lucide-react";
import { LinenDialog } from "@/components/LinenDialog";
import { GoogleImportDialog } from "@/components/GoogleImportDialog";
import { SesButton } from "@/components/SesButton";
import { CashToggle } from "@/components/CashToggle";
import { formatDate, formatDateTime } from "@/lib/date";
import { toast } from "sonner";

import { HistoryJumpBar } from "@/components/HistoryJumpBar";
import { BookingTimeDialog } from "@/components/BookingTimeDialog";
import { IntakeSummary } from "@/components/IntakeSummary";
import { MakeReservationDialog } from "@/components/MakeReservationDialog";
import { tx, lz } from "@/i18n";
import { useOriginals, OrigToggle } from "@/components/OrigToggle";
const empty = { house_id: "", title: "", type: "checkin", start: "", end: "", guest_name: "", assignee_id: "", notes: "", status: "scheduled", cash: false };

// Subtiele pastel-accenten per boekingstype (linker rand + lichte tint)
const TYPE_CARD = {
  checkin: "border-l-4 border-l-emerald-300 bg-emerald-50/40",
  checkout: "border-l-4 border-l-amber-300 bg-amber-50/40",
  cleaning: "border-l-4 border-l-sky-300 bg-sky-50/40",
  maintenance: "border-l-4 border-l-violet-300 bg-violet-50/40",
  inspection: "border-l-4 border-l-orange-300 bg-orange-50/40",
  own_before: "border-l-4 border-l-violet-200 bg-violet-50/40",
  own_after: "border-l-4 border-l-violet-200 bg-violet-50/40",
};

const GUEST_TYPES = ["checkin", "cleaning", "checkout"];
const taskComplete = (t) => !!t && (t.status === "done" || (Array.isArray(t.subtasks) && t.subtasks.length > 0 && t.subtasks.every((s) => s.done)));
// Status-icoontjes per boeking: groen zodra de gekoppelde checklist voltooid is
const STATUS_ICONS = lz([
  { cat: "checkin", Icon: Key, label: "Incheck" },
  { cat: "checkout", Icon: LogOut, label: "Uitcheck" },
  { cat: "cleaning", Icon: Sparkles, label: "Schoonmaak" },
]);
const BABY_RE = /baby|kinderstoel|babybedje|campingbedje/i;
const needsBaby = (b) => BABY_RE.test(`${b.party_composition || ""} ${b.res_notes || ""}`);

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
// Linnen-alert: uitcheck zonder linnen-registratie, na 22:00 op de uitcheckdag (of dag al voorbij)
const linenOverdue = (b) => {
  if (b.type !== "checkout" || b.linen_done !== false || !b.start) return false;
  const t = new Date(b.start); t.setHours(22, 0, 0, 0);
  return Date.now() >= t.getTime();
};
// Checklist-alert: 24u na starttijd nog niet volledig afgevinkt
const isNight = (b) => {
  if (!["checkin", "checkout"].includes(b.type) || !b.start || !b.start.includes("T") || b.night_fee === 0) return false;
  const h = new Date(b.start).getHours();
  return h >= 22 || h < 7;
};
const checklistOverdue = (b, ct, done) => !!ct && !done && !!b.start && (Date.now() - new Date(b.start).getTime() >= 24 * 3600 * 1000);
// SES rood: incheck met SES-aanmelding en status 'niets gedaan' (0)
const sesRed = (b) => b.type === "checkin" && !!b.register_ses && (b.ses_status || 0) === 0;
// Linnen-alert voor veldpersoneel (taken-weergave)
const taskLinenOverdue = (t) => {
  if (t.category !== "checkout" || t.linen_done !== false) return false;
  const d = t.start || t.due_date;
  if (!d) return false;
  const x = new Date(d); x.setHours(22, 0, 0, 0);
  return Date.now() >= x.getTime();
};

function fmt(dt) {
  return formatDateTime(dt);
}

export default function CalendarPage() {
  const orig = useOriginals();
  const { user } = useAuth();
  const { data: notif, refresh: refreshNotif } = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [houses, setHouses] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [gcal, setGcal] = useState({ connected: false, email: null });
  const [connecting, setConnecting] = useState(false);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [calList, setCalList] = useState({ calendars: [], pull: ["primary"], push: "primary" });
  const [calOpen, setCalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [timeEdit, setTimeEdit] = useState(null);
  const [localPull, setLocalPull] = useState(["primary"]);
  const [localPush, setLocalPush] = useState("primary");
  const [savingCal, setSavingCal] = useState(false);
  const [commentBooking, setCommentBooking] = useState(null);
  const [commentTab, setCommentTab] = useState("comments");
  const [pendingReports, setPendingReports] = useState([]);
  const [reportSettingsOpen, setReportSettingsOpen] = useState(false);
  const [notices, setNotices] = useState({});
  const [agendaDetails, setAgendaDetails] = useState({});
  const [search, setSearch] = useState("");
  const [detailBooking, setDetailBooking] = useState(null);
  const [makeResFor, setMakeResFor] = useState(null);
  const addChecklist = async (b) => {
    try {
      await api.post(`/bookings/${b.id}/checklist`);
      toast.success(tx("Checklist toegevoegd"));
      setDetailBooking(null);
      load(); loadTasks();
    } catch (err) { toast.error(apiError(err)); }
  };
  const [openChecklist, setOpenChecklist] = useState({});
  const [openNotes, setOpenNotes] = useState({});
  const canManage = ["admin"].includes(user.role);
  const isTeam = ["admin", "host"].includes(user.role);
  const isFieldStaff = ["cleaner", "subcontractor"].includes(user.role);
  const logsHours = ["host", "cleaner", "subcontractor"].includes(user.role);
  const [linenFor, setLinenFor] = useState(null);
  const [hoursEdit, setHoursEdit] = useState({});
  const [workerEdit, setWorkerEdit] = useState({});
  const [myHours, setMyHours] = useState(null);  const staffWithRate = staff.filter((s) => s.hourly_rate != null && s.hourly_rate !== "");

  const loadAgendaDetails = () => api.get("/houses-agenda-details").then(({ data }) => setAgendaDetails(data || {})).catch(() => setAgendaDetails({}));
  const houseInfo = (hid, key) => (agendaDetails[hid] || []).length > 0 && (
    <div className="mt-2 flex flex-col gap-1" data-testid={`agenda-house-details-${key}`}>
      {agendaDetails[hid].map((d) => (
        <span key={d.id} className="inline-flex items-start gap-1.5 text-xs px-2 py-1 rounded-md w-fit max-w-full bg-sky-50 text-sky-900 border border-sky-100" data-testid={`agenda-house-detail-${key}-${d.id}`}>
          <Info className="h-3 w-3 shrink-0 mt-0.5" /><span className="break-words"><span className="font-semibold">{d.label}</span>{d.label && d.value ? ": " : ""}{d.value}</span>
        </span>
      ))}
    </div>
  );
  const loadNotices = () => api.get("/notices/active").then(({ data }) => setNotices(data || {})).catch(() => setNotices({}));

  const loadGoogleEvents = () => api.get("/calendar/google-events").then(({ data }) => setGoogleEvents(data)).catch(() => setGoogleEvents([]));
  const loadCalList = () => api.get("/calendar/list").then(({ data }) => setCalList(data)).catch(() => {});
  const loadPendingReports = () => api.get("/reports", { params: { status: "submitted" } }).then(({ data }) => setPendingReports(data)).catch(() => setPendingReports([]));
  const openComments = (b, tab = "comments") => { setCommentTab(tab); setCommentBooking(b); };
  const load = () => api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => setBookings([]));
  const reassign = async (b, aid) => {
    setBookings((p) => (p || []).map((x) => (x.id === b.id ? { ...x, assignee_id: aid || null } : x)));
    try {
      await api.patch(`/bookings/${b.id}/assignee`, { assignee_id: aid || null });
      toast.success(aid === user.id ? tx("Je hebt deze afspraak overgenomen") : tx("Afspraak opnieuw toegewezen"));
      load(); loadTasks();
    } catch (e) { toast.error(apiError(e)); }
  };
  const loadTasks = () => api.get("/tasks").then(({ data }) => setTasks(data)).catch(() => setTasks([]));
  const loadMyHours = (month) => api.get("/me/hours-summary", { params: month ? { month } : {} }).then(({ data }) => setMyHours(data)).catch(() => {});
  const _now = new Date();
  const curMonthStr = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}`;
  const _prev = new Date(_now.getFullYear(), _now.getMonth() - 1, 1);
  const prevMonthStr = `${_prev.getFullYear()}-${String(_prev.getMonth() + 1).padStart(2, "0")}`;
  const shiftHoursMonth = (delta) => {
    const base = myHours?.month;
    if (!base) return;
    let [y, m] = base.split("-").map(Number);
    m += delta;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    const target = `${y}-${String(m).padStart(2, "0")}`;
    if (target < prevMonthStr || target > curMonthStr) return;
    loadMyHours(target);
  };
  useEffect(() => {
    if (logsHours) loadMyHours();
    loadAgendaDetails();
    if (isFieldStaff) { loadTasks(); loadNotices(); return; }
    load();
    loadTasks();
    loadGoogleEvents();
    loadNotices();
    if (canManage) {
      loadPendingReports();
      api.get("/houses").then(({ data }) => setHouses(data)).catch(() => {});
      api.get("/users").then(({ data }) => setStaff(data.filter((u) => ["cleaner", "subcontractor", "host"].includes(u.role)))).catch(() => {});
      api.get("/oauth/calendar/status").then(({ data }) => { setGcal(data); if (data.connected) loadCalList(); else if (data.expired) toast.info(tx("Je Google Agenda-koppeling is verlopen. Koppel opnieuw om de agenda's te zien.")); }).catch(() => {});
    }
  }, []);

  const openCalSettings = () => { setLocalPull(calList.pull || ["primary"]); setLocalPush(calList.push || "primary"); setCalOpen(true); };
  const togglePull = (id) => setLocalPull((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const saveCalSettings = async () => {
    setSavingCal(true);
    try {
      await api.post("/calendar/settings", { pull: localPull.length ? localPull : ["primary"], push: localPush });
      toast.success(tx("Agenda-instellingen opgeslagen"));
      setCalOpen(false);
      await loadCalList();
      await loadGoogleEvents();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingCal(false); }
  };

  useEffect(() => {
    const c = searchParams.get("calendar");
    if (c === "connected") { toast.success(tx("Google Agenda gekoppeld!")); setGcal((g) => ({ ...g, connected: true })); api.get("/oauth/calendar/status").then(({ data }) => setGcal(data)).catch(() => {}); loadCalList(); loadGoogleEvents(); searchParams.delete("calendar"); setSearchParams(searchParams, { replace: true }); }
    else if (c === "error") { toast.error(tx("Koppelen met Google Agenda mislukt. Probeer opnieuw.")); searchParams.delete("calendar"); setSearchParams(searchParams, { replace: true }); }
  }, []);

  useEffect(() => {
    const bid = searchParams.get("booking");
    if (!bid || !bookings) return;
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="booking-${bid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-primary", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-primary", "ring-offset-2"), 2000);
      }
    }, 300);
    searchParams.delete("booking");
    setSearchParams(searchParams, { replace: true });
  }, [bookings]);

  const connectGcal = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/oauth/calendar/login");
      // Open in a new top-level tab: Google refuses to render its consent screen
      // inside the preview iframe (results in a generic 403 page).
      const win = window.open(data.authorization_url, "_blank", "noopener,noreferrer");
      if (!win) {
        toast.error(tx("Sta pop-ups toe om te koppelen, of open de app in een eigen tabblad."));
        setConnecting(false);
        return;
      }
      toast.info(tx("Google Agenda opent in een nieuw tabblad. Rond het koppelen daar af."));
      // Poll for connection status while the user completes OAuth in the other tab.
      let tries = 0;
      const timer = setInterval(async () => {
        tries += 1;
        try {
          const { data: st } = await api.get("/oauth/calendar/status");
          if (st.connected) {
            clearInterval(timer);
            setConnecting(false);
            setGcal(st);
            toast.success(tx("Google Agenda gekoppeld!"));
            loadCalList(); loadGoogleEvents();
          }
        } catch { /* keep polling */ }
        if (tries >= 60) { clearInterval(timer); setConnecting(false); }
      }, 3000);
    } catch (err) { toast.error(apiError(err)); setConnecting(false); }
  };

  const disconnectGcal = async () => {
    try { await api.post("/oauth/calendar/disconnect"); setGcal({ connected: false, email: null }); setGoogleEvents([]); toast.success(tx("Google Agenda ontkoppeld")); }
    catch (err) { toast.error(apiError(err)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/bookings", { ...form, assignee_id: form.assignee_id || null, end: form.end || null });
      toast.success(tx("Afspraak gepland"));
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/bookings/${id}`); load(); } catch (err) { toast.error(apiError(err)); }
  };

  const cycleSes = async (resId) => {
    try { await api.patch(`/reservations/${resId}/ses-status`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const setStatus = async (id, status) => {
    setBookings((p) => (p || []).map((x) => (x.id === id ? { ...x, status } : x)));
    try { await api.patch(`/bookings/${id}/status`, null, { params: { status } }); load(); loadTasks(); toast.success(tx("Status bijgewerkt")); }
    catch (err) { toast.error(apiError(err)); }
  };

  const toggleCash = async (id, current) => {
    try { await api.patch(`/bookings/${id}/cash`, null, { params: { cash: !current } }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const saveHours = async (ct, wbOverride) => {
    const rawH = hoursEdit[ct.id] ?? (ct.worked_hours ?? "");
    const wb = wbOverride !== undefined ? wbOverride : (workerEdit[ct.id] ?? (ct.worked_by || ct.assignee_id || ""));
    const v = rawH === "" ? null : Number(rawH);
    if (v != null && (isNaN(v) || v < 0)) return;
    const params = {};
    if (v != null) params.hours = v;
    if (wb) params.worked_by = wb;
    try { await api.patch(`/tasks/${ct.id}/hours`, null, { params }); load(); loadTasks(); loadMyHours(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const houseName = (hid) => houses.find((h) => h.id === hid)?.name || tx("Woning");
  const bookingTitle = (b) => bookingDisplayTitle(b, houses.find((h) => h.id === b.house_id)?.name);
  const ownBadge = (b, tid) => isOwnStay(b) && <Badge variant="outline" className="text-xs border-secondary text-secondary" data-testid={tid}>{tx("Eigen verblijf")}</Badge>;
  const taskFor = (resId, cat) => (tasks || []).find((t) => t.reservation_id === resId && t.category === cat);
  const isGuestBooking = (b) => !!b.reservation_id && GUEST_TYPES.includes(b.type);
  const linkedTask = (b) => (b.task_id ? (tasks || []).find((t) => t.id === b.task_id) || null : isGuestBooking(b) ? taskFor(b.reservation_id, b.type) : null);

  const setTaskStatus = async (id, status) => {
    try { await api.patch(`/tasks/${id}/status`, null, { params: { status } }); loadTasks(); toast.success(tx("Status bijgewerkt")); }
    catch (err) { toast.error(apiError(err)); }
  };
  const toggleSub = async (taskId, index, done) => {
    try { await api.patch(`/tasks/${taskId}/subtask`, null, { params: { index, done } }); loadTasks(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const q = search.trim().toLowerCase();
  const matchTask = (t) => {
    if (!q) return true;
    const hay = [t.title, t.house_name, BOOKING_TYPES[t.category]?.label].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  };
  const matchBooking = (b) => {
    if (!q) return true;
    const hay = [b.title, b.guest_name, houseName(b.house_id), b.notes, b.res_notes, b.party_composition, BOOKING_TYPES[b.type]?.label]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  };
  const matchGevent = (e) => {
    if (!q) return true;
    const hay = [e.title, e.description, e.location, e.calendar].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  };
  const filteredBookings = bookings ? bookings.filter(matchBooking) : bookings;
  const filteredGoogleEvents = googleEvents.filter(matchGevent);
  const filteredTasks = tasks ? tasks.filter(matchTask) : tasks;

  const [histExpanded, setHistExpanded] = useState(() => new Set());
  const [historyOpen, setHistoryOpen] = useState(false);
  const bookingCard = (b) => {
    const bt = BOOKING_TYPES[b.type] || BOOKING_TYPES.checkin;
    const displayTitle = bookingTitle(b);
    const ct = linkedTask(b);
    const cdone = taskComplete(ct);
    const hoursFilled = !!ct && ((ct.worked_hours != null && ct.worked_hours !== "") || !!ct.hours_filled);
    const fullyDone = cdone && hoursFilled;
    const canChat = isTeam && b.reservation_id;
    const lAlert = linenOverdue(b);
    const clAlert = checklistOverdue(b, ct, cdone);
    const sAlert = canManage && sesRed(b);
    return (
      <div key={b.id} className={`bg-card rounded-xl border shadow-sm p-5 flex items-center gap-4 flex-wrap ${(lAlert || clAlert || sAlert) ? "border-red-300 ring-1 ring-red-200" : "border-border"} ${TYPE_CARD[b.type] || TYPE_CARD.checkin}`} data-testid={`booking-${b.id}`}>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${bt.color}`}>{bt.label}</span>
            <button type="button" onClick={() => setDetailBooking(b)} className="font-display font-semibold text-left hover:underline" data-testid={`booking-open-${b.id}`}>{displayTitle}</button>
            {ownBadge(b, `booking-eigen-${b.id}`)}
            {b.house_address_url && (
              <a href={b.house_address_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary" title={tx("Open locatie in Maps")} data-testid={`booking-map-${b.id}`}>
                <MapPin className="h-4 w-4" />
              </a>
            )}
            {isNight(b) && <span title={tx("Late in- of uitcheck (nachttoeslag)")} className="text-indigo-600" data-testid={`booking-night-${b.id}`}><Moon className="h-4 w-4" /></span>}
            {needsBaby(b) && <span title={tx("Babyvoorziening gevraagd (baby/kinderstoel/campingbedje)")} className="text-lg leading-none" data-testid={`booking-baby-${b.id}`}>🍼</span>}
            {b.type === "checkin" && b.register_ses && <SesButton status={b.ses_status} canEdit={canManage} onCycle={() => cycleSes(b.reservation_id)} testId={`booking-ses-${b.id}`} />}
            <CashToggle value={b.cash} canEdit={canManage} onToggle={() => toggleCash(b.id, b.cash)} testId={`booking-cash-${b.id}`} />
            {b.status === "done" && <Badge className="bg-secondary text-white">{tx("Voltooid")}</Badge>}
          </div>
          <div className="flex items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-2 flex-wrap">
            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {fmt(b.start)}</span>
            {canManage && (
              <label className="flex items-center gap-1.5" title={tx("Toegewezen aan")}>
                <UserCog className="h-3.5 w-3.5" />
                <select value={b.assignee_id || ""} onChange={(e) => reassign(b, e.target.value)}
                  className={`h-7 rounded border bg-background px-1.5 text-sm max-w-[170px] ${b.assignee_id ? "border-input text-foreground" : "border-amber-400 text-amber-700"}`}
                  data-testid={`booking-assignee-select-${b.id}`}>
                  <option value="">{tx("Niet toegewezen")}</option>
                  <option value={user.id}>{tx("Ikzelf")}</option>
                  {staff.map((s) => <option key={s.id} value={s.id}>{s.name} · {ROLE_LABELS[s.role]}</option>)}
                </select>
              </label>
            )}
            {b.type !== "cleaning" && b.guest_name && !isOwnStay(b) && (canChat ? (
              <button type="button" onClick={() => navigate(`/app/chat?res=${b.reservation_id}`)} className="flex items-center gap-1.5 text-primary hover:underline font-medium" data-testid={`booking-guest-chat-${b.id}`}>
                <User className="h-3.5 w-3.5" /> {b.guest_name}
              </button>
            ) : (
              <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {b.guest_name}</span>
            ))}
            {b.type !== "cleaning" && b.guest_phone && (
              <a href={`https://wa.me/${b.guest_phone.replace(/\D/g, "").replace(/^00/, "")}`} target="_blank" rel="noopener noreferrer" title={tx("Bellen via WhatsApp")} className="flex items-center gap-1.5 text-primary hover:underline" data-testid={`booking-phone-${b.id}`}>
                <Phone className="h-3.5 w-3.5 text-[#25D366] fill-current" /> {b.guest_phone}
              </a>
            )}
            {b.type !== "cleaning" && b.num_guests != null && (
              <span className="flex items-center gap-1.5" data-testid={`booking-guests-${b.id}`}><Users className="h-3.5 w-3.5" /> {b.num_guests} pers.</span>
            )}
            {b.type === "cleaning" && b.next_checkin && (
              <span className="flex items-center gap-1.5" data-testid={`booking-next-checkin-${b.id}`}>
                <Clock className="h-3.5 w-3.5" /> {tx("klaar vóór")} {fmt(b.next_checkin.start)}
                {b.next_checkin.num_guests != null && <span className="inline-flex items-center gap-1.5"> · <Users className="h-3.5 w-3.5" /> {b.next_checkin.num_guests} pers.</span>}
              </span>
            )}
          </div>
          <IntakeSummary booking={b} compact />
          {houseInfo(b.house_id, b.id)}
          {(notices[b.house_id] || []).length > 0 && (
            <div className="mt-2 flex flex-col gap-1" data-testid={`booking-notices-${b.id}`}>
              {(notices[b.house_id] || []).map((n) => (
                <span key={n.id} className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md w-fit max-w-full ${n.priority === "urgent" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`} title={n.text} data-testid={`booking-notice-${n.id}`}>
                  <Megaphone className="h-3 w-3 shrink-0" />
                  <span className="truncate max-w-[320px]">{n.text}</span>
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-wrap justify-end">
          <Button size="sm" variant="ghost" onClick={() => openComments(b, canManage && pendingReports.some((r) => r.booking_id === b.id) ? "report" : "comments")} className="gap-1.5 relative text-muted-foreground" data-testid={`booking-comments-${b.id}`}>
            <MessageSquare className="h-4 w-4" />
            {canManage && pendingReports.some((r) => r.booking_id === b.id) && (
              <span className="absolute -top-0.5 -left-0.5 h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-card" title={tx("Rapport wacht op controle")} data-testid={`booking-report-pending-${b.id}`} />
            )}
            {notif.bookings?.[b.id] > 0 && (
              <span className="absolute -top-0.5 -right-0.5 grid place-items-center min-w-[16px] h-4 px-1 rounded-full bg-destructive text-white text-[10px] font-bold" data-testid={`booking-unread-${b.id}`}>{notif.bookings[b.id]}</span>
            )}
          </Button>
          {b.type === "checkout" && (
            <Button size="sm" variant="outline" onClick={() => setLinenFor({ houseId: b.house_id, houseName: houseName(b.house_id), reservationId: b.reservation_id })} className={`gap-1.5 ${lAlert ? "border-red-500 text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700" : b.linen_done ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 hover:text-emerald-900" : ""}`} title={lAlert ? tx("Linnen nog niet geregistreerd op de uitcheckdag") : b.linen_done ? tx("Linnen is ingevuld") : undefined} data-testid={`booking-linen-${b.id}`}>
              <Shirt className="h-4 w-4" /> {tx("Linnen")}
            </Button>
          )}
          {canManage && (b.status !== "done" ? (
            <Button size="sm" variant="outline" onClick={() => setStatus(b.id, "done")} className="border-secondary text-secondary hover:bg-secondary/10" data-testid={`booking-done-${b.id}`}>{tx("Markeer voltooid")}</Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setStatus(b.id, "scheduled")} data-testid={`booking-reopen-${b.id}`}>{tx("Heropenen")}</Button>
          ))}
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => setTimeEdit(b)} className="gap-1.5" data-testid={`booking-edit-time-${b.id}`}>
              <Clock className="h-4 w-4" /> {tx("Tijden")}
            </Button>
          )}
          {canManage && <Button size="icon" variant="ghost" onClick={() => del(b.id)} className="text-destructive" data-testid={`booking-delete-${b.id}`}><Trash2 className="h-4 w-4" /></Button>}
        </div>
        {ct && (
          <div className="basis-full w-full border-t border-border pt-3 mt-1" data-testid={`booking-checklist-${b.id}`}>
            <button type="button" onClick={() => setOpenChecklist((p) => ({ ...p, [b.id]: !p[b.id] }))} className="flex items-center gap-2 text-sm font-medium w-full" data-testid={`booking-checklist-toggle-${b.id}`}>
              <ListChecks className={`h-4 w-4 ${fullyDone ? "text-emerald-600" : "text-muted-foreground"}`} /> {tx("Checklist")}
              {fullyDone && <Badge className="bg-emerald-600 text-white">{tx("Voltooid")}</Badge>}
              {cdone && !hoursFilled && <Badge className="bg-red-600 text-white" data-testid={`booking-hours-needed-${b.id}`}>{tx("Niet voltooid")}</Badge>}
              {clAlert && <Badge className="bg-red-600 text-white" data-testid={`booking-checklist-alert-${b.id}`}>{tx("Niet voltooid")}</Badge>}
              {openChecklist[b.id] ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
            </button>
            {openChecklist[b.id] && (
              <div className="mt-2.5">
                {Array.isArray(ct.subtasks) && ct.subtasks.length > 0 ? (
                  <div className="space-y-1.5">
                    <OrigToggle t={ct} o={orig} />
                    {ct.subtasks.map((s, i) => (
                      <label key={i} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`booking-sub-${b.id}-${i}`}>
                        <Checkbox checked={!!s.done} onCheckedChange={(v) => toggleSub(ct.id, i, !!v)} className="h-4 w-4" />
                        <span className={s.done ? "line-through text-muted-foreground" : ""}>{orig.sub(ct, s)}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  cdone
                    ? <Button size="sm" variant="ghost" onClick={() => setTaskStatus(ct.id, "open")} data-testid={`booking-reopen-task-${b.id}`}>{tx("Checklist heropenen")}</Button>
                    : <Button size="sm" variant="outline" onClick={() => setTaskStatus(ct.id, "done")} className="border-secondary text-secondary hover:bg-secondary/10 gap-1.5" data-testid={`booking-done-task-${b.id}`}><Check className="h-4 w-4" /> {tx("Markeer checklist voltooid")}</Button>
                )}
                {ct.hours_hidden ? null : (canManage || ["host", "cleaner", "subcontractor"].includes(user.role)) ? (
                  <div className="mt-3 flex items-center gap-2 text-sm flex-wrap" data-testid={`booking-hours-wrap-${b.id}`}>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{tx("Gewerkte uren:")}</span>
                    <input
                      type="number" min="0" step="0.25"
                      value={hoursEdit[ct.id] ?? (ct.worked_hours ?? "")}
                      onChange={(e) => setHoursEdit((p) => ({ ...p, [ct.id]: e.target.value }))}
                      onBlur={() => saveHours(ct)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      placeholder={tx("uren")}
                      className="w-20 h-7 rounded border border-input bg-background px-2 text-sm"
                      data-testid={`booking-hours-input-${b.id}`}
                    /> {tx("u")}
                    {canManage && (
                      <select
                        value={workerEdit[ct.id] ?? (ct.worked_by || ct.assignee_id || "")}
                        onChange={(e) => { setWorkerEdit((p) => ({ ...p, [ct.id]: e.target.value })); saveHours(ct, e.target.value); }}
                        className="h-7 rounded border border-input bg-background px-1.5 text-sm max-w-[160px]"
                        data-testid={`booking-worker-select-${b.id}`}
                        title={tx("Wie heeft deze uren gewerkt (voor loonkosten)")}>
                        <option value="">{tx("— medewerker —")}</option>
                        {staffWithRate.map((s) => <option key={s.id} value={s.id}>{`${s.name} (€${s.hourly_rate})`}</option>)}
                      </select>
                    )}
                  </div>
                ) : (ct.worked_hours != null && (
                  <div className="mt-3 flex items-center gap-1.5 text-sm text-muted-foreground" data-testid={`booking-hours-${b.id}`}><Clock className="h-4 w-4" /> {tx("Gewerkte uren:")} {ct.worked_hours} u</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const today0 = startOfDay(new Date());
  const bookingHasWarning = (b) => {
    const ct = linkedTask(b);
    return linenOverdue(b) || checklistOverdue(b, ct, taskComplete(ct));
  };
  const attention = [], upcoming = [], history = [];
  (filteredBookings || []).forEach((b) => {
    const isPast = b.start && startOfDay(b.start) < today0;
    // Aandachtspunt pas zodra de dag van de afspraak voorbij is
    if (isPast && b.status !== "done" && (bookingHasWarning(b) || (canManage && sesRed(b)))) attention.push(b);
    else if (isPast) history.push(b);
    else upcoming.push(b);
  });
  attention.sort((a, c) => new Date(a.start) - new Date(c.start));
  upcoming.sort((a, c) => new Date(a.start) - new Date(c.start));
  history.sort((a, c) => new Date(c.start) - new Date(a.start));

  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">{tx("Agenda")}</h1>
          <p className="text-muted-foreground mt-1">{isFieldStaff ? tx("Jouw toegewezen taken met woning en tijd.") : tx("Sleuteloverdrachten, schoonmaak en onderhoud.")}</p>
          {logsHours && myHours && (
            <div className="text-xs text-muted-foreground/80 mt-1.5 flex items-center gap-2 flex-wrap" data-testid="my-hours-summary">
              <button type="button" onClick={() => shiftHoursMonth(-1)} disabled={myHours.month <= prevMonthStr} className="p-0.5 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:pointer-events-none" title={tx("Vorige maand")} data-testid="hours-prev-month"><ChevronLeft className="h-3.5 w-3.5" /></button>
              <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> <span className="font-medium text-foreground/70" data-testid="hours-month-label">{myHours.month === curMonthStr ? tx("deze maand") : tx("vorige maand")}</span></span>
              <button type="button" onClick={() => shiftHoursMonth(1)} disabled={myHours.month >= curMonthStr} className="p-0.5 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:pointer-events-none" title={tx("Volgende maand")} data-testid="hours-next-month"><ChevronRight className="h-3.5 w-3.5" /></button>
              <span className="text-muted-foreground/40">·</span>
              <span className="inline-flex items-center gap-2.5">
                <span className="font-semibold text-foreground/70" data-testid="hours-regular">{myHours.hours_regular ?? 0} u</span>
                <span className="inline-flex items-center gap-1" data-testid="hours-cash"><span className="font-semibold text-foreground/70">{myHours.hours_cash ?? 0} u</span> <span className="text-foreground/70 leading-none">●</span></span>
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canManage && (
            <Button variant="outline" size="sm" onClick={() => setReportSettingsOpen(true)} className="gap-1.5" data-testid="report-settings-btn">
              <FileText className="h-4 w-4" /> {tx("Rapportage")}
            </Button>
          )}
          {canManage && (
            gcal.connected ? (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-secondary bg-secondary/10 px-3 py-2 rounded-lg" data-testid="gcal-connected">
                  <Check className="h-4 w-4" /> {tx("Google Agenda")}{gcal.email ? `: ${gcal.email}` : ""}
                </span>
                <Dialog open={calOpen} onOpenChange={setCalOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" onClick={openCalSettings} className="gap-1.5 border-secondary/40 text-secondary hover:bg-secondary/10" data-testid="gcal-manage-btn">
                      <Settings className="h-4 w-4" /> {tx("Agenda's")}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-display">{tx("Welke agenda's gebruiken?")}</DialogTitle></DialogHeader>
                    {calList.calendars.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4">{tx("Geen agenda's gevonden.")}</p>
                    ) : (
                      <div className="space-y-5">
                        <div>
                          <div className="flex items-center gap-2 text-sm font-semibold mb-2"><Download className="h-4 w-4 text-secondary" /> {tx("Inlezen in portaal")}</div>
                          <p className="text-xs text-muted-foreground mb-3">{tx("Vink de agenda's aan waarvan je de afspraken in het portaal wilt zien.")}</p>
                          {calList.pull_disabled && <p className="text-xs text-amber-700 mb-3" data-testid="pull-disabled-note">{tx("Inlezen staat uit sinds de import. Google afspraken worden niet meer in het portaal getoond.")}</p>}
                          <div className="space-y-2">
                            {calList.calendars.map((c) => (
                              <label key={c.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-border hover:bg-accent cursor-pointer" data-testid={`pull-cal-${c.id}`}>
                                <Checkbox checked={localPull.includes(c.id)} onCheckedChange={() => togglePull(c.id)} />
                                <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                                <span className="text-sm flex-1 truncate">{c.summary}{c.primary ? tx(" (hoofd)") : ""}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 text-sm font-semibold mb-2"><Upload className="h-4 w-4 text-primary" /> {tx("Afspraken hierop zetten")}</div>
                          <p className="text-xs text-muted-foreground mb-3">{tx("Nieuwe Costa Key afspraken worden naar deze agenda geschreven.")}</p>
                          <RadioGroup value={localPush} onValueChange={setLocalPush} className="space-y-2">
                            {calList.calendars.filter((c) => c.access === "owner" || c.access === "writer" || c.primary).map((c) => (
                              <label key={c.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-border hover:bg-accent cursor-pointer" data-testid={`push-cal-${c.id}`}>
                                <RadioGroupItem value={c.id} />
                                <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                                <span className="text-sm flex-1 truncate">{c.summary}{c.primary ? tx(" (hoofd)") : ""}</span>
                              </label>
                            ))}
                          </RadioGroup>
                        </div>
                      </div>
                    )}
                    <DialogFooter>
                      <Button onClick={saveCalSettings} disabled={savingCal} className="bg-primary hover:bg-primary/90" data-testid="save-cal-settings-btn">
                        {savingCal && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {tx("Opslaan")}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} className="gap-1.5" data-testid="gcal-import-btn">
                  <Download className="h-4 w-4" /> {tx("Importeren")}
                </Button>
                <Button variant="ghost" size="sm" onClick={disconnectGcal} className="text-muted-foreground" data-testid="gcal-disconnect-btn">{tx("Ontkoppelen")}</Button>
              </div>
            ) : (
              <Button variant="outline" onClick={connectGcal} disabled={connecting} className="border-secondary text-secondary hover:bg-secondary/10 gap-2" data-testid="gcal-connect-btn">
                {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />} {tx("Koppel Google Agenda")}
              </Button>
            )
          )}
          {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90 gap-2" data-testid="add-booking-btn"><Plus className="h-4 w-4" /> {tx("Afspraak")}</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">{tx("Nieuwe afspraak")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{tx("Titel *")}</Label><Input value={form.title} onChange={set("title")} className="mt-1" data-testid="booking-title-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{tx("Type")}</Label>
                    <Select value={form.type} onValueChange={set("type")}>
                      <SelectTrigger className="mt-1" data-testid="booking-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(BOOKING_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{tx("Woning *")}</Label>
                    <Select value={form.house_id} onValueChange={set("house_id")}>
                      <SelectTrigger className="mt-1" data-testid="booking-house-select"><SelectValue placeholder={tx("Kies")} /></SelectTrigger>
                      <SelectContent>{houses.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{tx("Start *")}</Label><Input type="datetime-local" value={form.start} onChange={set("start")} className="mt-1" data-testid="booking-start-input" /></div>
                  <div><Label>{tx("Einde")}</Label><Input type="datetime-local" value={form.end} onChange={set("end")} className="mt-1" /></div>
                </div>
                <div><Label>{tx("Gastnaam")}</Label><Input value={form.guest_name} onChange={set("guest_name")} className="mt-1" /></div>
                <div>
                  <Label>{tx("Toegewezen aan")}</Label>
                  <Select value={form.assignee_id} onValueChange={set("assignee_id")}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder={tx("Optioneel")} /></SelectTrigger>
                    <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {ROLE_LABELS[s.role]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{tx("Notities")}</Label><Textarea value={form.notes} onChange={set("notes")} className="mt-1" /></div>
                <div className="flex justify-end pt-1">
                  <CashToggle value={form.cash} canEdit onToggle={() => set("cash")(!form.cash)} testId="booking-cash-toggle" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={save} disabled={saving || !form.title || !form.house_id || !form.start} className="bg-primary hover:bg-primary/90" data-testid="save-booking-btn">
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {tx("Opslaan")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={tx("Zoek op woning, gast of omschrijving…")}
          className="pl-9 pr-9"
          data-testid="agenda-search-input"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="agenda-search-clear">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isFieldStaff && (
        !tasks ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
        ) : tasks.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground" data-testid="agenda-no-tasks">
            <CalendarDays className="h-10 w-10 mx-auto mb-3 opacity-40" /> {tx("Nog geen taken toegewezen.")}
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground" data-testid="agenda-no-results">
            <Search className="h-10 w-10 mx-auto mb-3 opacity-40" /> {tx("Geen taken gevonden voor \"")}{search}".
          </div>
        ) : (
          <div className="space-y-3">
            {[...filteredTasks].sort((a, c) => (a.start || a.due_date || "9999").localeCompare(c.start || c.due_date || "9999")).map((t) => {
              const ownT = /eigen_verblijf|eigen verblijf/i.test(t.category || "") ? (/v[oó]{1,2}r/i.test(t.category) ? "own_before" : "own_after") : null;
              const bt = BOOKING_TYPES[ownT || t.category];
              const when = t.start ? `${fmt(t.start)}${t.end ? ` – ${fmt(t.end)}` : ""}` : formatDate(t.due_date);
              return (
                <div key={t.id} className={`bg-card rounded-xl border border-border shadow-sm p-5 ${TYPE_CARD[ownT || t.category] || "border-l-4 border-l-slate-300 bg-slate-50/40"}`} data-testid={`agenda-task-${t.id}`}>
                  <div className="flex items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${bt ? bt.color : "bg-slate-100 text-slate-600"}`}>{bt ? bt.label : tx("Taak")}</span>
                        <h3 className={`font-display font-semibold ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{taskDisplayTitle(t, orig.show(t, "title"))}</h3>
                        {ownT && <Badge variant="outline" className="text-xs border-secondary text-secondary" data-testid={`agenda-task-eigen-${t.id}`}>{tx("Eigen verblijf")}</Badge>}
                        {t.status === "done" && <Badge className="bg-secondary text-white">{tx("Voltooid")}</Badge>}
                      </div>
                      <OrigToggle t={t} o={orig} />
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2 flex-wrap">
                        {when && <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {when}</span>}
                        {t.house_name && <span className="flex items-center gap-1.5"><HomeIcon className="h-3.5 w-3.5" /> {t.house_name}</span>}
                      </div>
                      {houseInfo(t.house_id, t.id)}
                      {(notices[t.house_id] || []).length > 0 && (
                        <div className="mt-2 flex flex-col gap-1" data-testid={`agenda-task-notices-${t.id}`}>
                          {(notices[t.house_id] || []).map((n) => (
                            <span key={n.id} className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md w-fit max-w-full ${n.priority === "urgent" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`} title={n.text}>
                              <Megaphone className="h-3 w-3 shrink-0" /><span className="truncate max-w-[320px]">{n.text}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {t.category === "checkout" && <Button size="sm" variant="outline" onClick={() => setLinenFor({ houseId: t.house_id, houseName: t.house_name, reservationId: t.reservation_id })} className={`gap-1.5 ${taskLinenOverdue(t) ? "border-red-500 text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700" : t.linen_done ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 hover:text-emerald-900" : ""}`} title={taskLinenOverdue(t) ? tx("Linnen nog niet geregistreerd op de uitcheckdag") : undefined} data-testid={`agenda-task-linen-${t.id}`}><Shirt className="h-4 w-4" /> {tx("Linnen")}</Button>}
                      {t.status !== "done" ? (
                        <Button size="sm" variant="outline" onClick={() => setTaskStatus(t.id, "done")} className="border-secondary text-secondary hover:bg-secondary/10 gap-1.5" data-testid={`agenda-task-done-${t.id}`}><Check className="h-4 w-4" /> {tx("Afvinken")}</Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => setTaskStatus(t.id, "open")} data-testid={`agenda-task-reopen-${t.id}`}>{tx("Heropenen")}</Button>
                      )}
                    </div>
                  </div>
                  {t.subtasks && t.subtasks.length > 0 && (
                    <div className="mt-3 space-y-1.5 border-t border-border pt-3">
                      {t.subtasks.map((s, i) => (
                        <label key={i} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`agenda-subtask-${t.id}-${i}`}>
                          <Checkbox checked={!!s.done} onCheckedChange={(v) => toggleSub(t.id, i, !!v)} className="h-4 w-4" />
                          <span className={s.done ? "line-through text-muted-foreground" : ""}>{orig.sub(t, s)}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-2 text-sm border-t border-border pt-3" data-testid={`agenda-task-hours-${t.id}`}>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{tx("Gewerkte uren:")}</span>
                    <input type="number" min="0" step="0.25" value={hoursEdit[t.id] ?? (t.worked_hours ?? "")}
                      onChange={(e) => setHoursEdit((p) => ({ ...p, [t.id]: e.target.value }))} onBlur={() => saveHours(t)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }} placeholder={tx("uren")}
                      className="w-20 h-7 rounded border border-input bg-background px-2 text-sm" data-testid={`agenda-task-hours-input-${t.id}`} /> {tx("u")}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {!isFieldStaff && (!bookings ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : bookings.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground">
          <CalendarDays className="h-10 w-10 mx-auto mb-3 opacity-40" /> {tx("Geen afspraken gepland.")}
        </div>
      ) : filteredBookings.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground" data-testid="agenda-no-results">
          <Search className="h-10 w-10 mx-auto mb-3 opacity-40" /> {tx("Geen afspraken gevonden voor \"")}{search}".
        </div>
      ) : (
        <div className="space-y-8">
          {canManage && pendingReports.length > 0 && (
            <section data-testid="agenda-reports-pending">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-5 w-5 text-primary" />
                <h2 className="font-display font-semibold text-xl">{tx("Te controleren")}</h2>
                <span className="text-xs text-muted-foreground">({pendingReports.length})</span>
              </div>
              <div className="space-y-2">
                {pendingReports.map((r) => {
                  const bk = (bookings || []).find((x) => x.id === r.booking_id);
                  return (
                    <button key={r.id} type="button" disabled={!bk} onClick={() => bk && openComments(bk, "report")} className="w-full text-left bg-card rounded-xl border border-red-200 ring-1 ring-red-100 p-4 flex items-center gap-3 flex-wrap hover:bg-muted/40 transition-colors" data-testid={`pending-report-${r.id}`}>
                      <span className="h-2.5 w-2.5 rounded-full bg-destructive" />
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${(BOOKING_TYPES[r.type] || BOOKING_TYPES.checkin).color}`}>{(BOOKING_TYPES[r.type] || BOOKING_TYPES.checkin).label}</span>
                      <span className="font-display font-semibold">{r.house_name}</span>
                      <span className="text-sm text-muted-foreground">{r.guest_name} · {fmt(r.booking_start)}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{tx("Ingediend door")} {r.submitted_by}{r.submitted_at ? ` · ${formatDateTime(r.submitted_at)}` : ""}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}
          <HistoryJumpBar count={history.length} upcomingId="agenda-upcoming-anchor" historyId="agenda-history-anchor" onOpenHistory={() => setHistoryOpen(true)} testPrefix="agenda" />
          {attention.length > 0 && (
            <section data-testid="agenda-attention">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <h2 className="font-display font-semibold text-xl">{tx("Aandachtspunten")}</h2>
                <span className="text-xs text-muted-foreground">({attention.length})</span>
              </div>
              <div className="space-y-3">{attention.map(bookingCard)}</div>
            </section>
          )}
          <section id="agenda-upcoming-anchor" data-testid="agenda-upcoming">
            {(attention.length > 0 || history.length > 0) && <h2 className="font-display font-semibold text-xl mb-3">{tx("Vanaf vandaag")}</h2>}
            {upcoming.length > 0 ? (
              <div className="space-y-3">{upcoming.map(bookingCard)}</div>
            ) : (
              <p className="text-sm text-muted-foreground py-4" data-testid="agenda-no-upcoming">{tx("Geen aankomende afspraken.")}</p>
            )}
          </section>
          {history.length > 0 && (
            <section id="agenda-history-anchor" data-testid="agenda-history">
              <button type="button" onClick={() => setHistoryOpen((o) => !o)} className="flex items-center gap-2 mb-3 w-full text-left" data-testid="agenda-history-toggle">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <h2 className="font-display font-semibold text-xl">{tx("Historie")}</h2>
                <span className="text-xs text-muted-foreground">({history.length})</span>
                {historyOpen ? <ChevronUp className="h-5 w-5 ml-auto text-muted-foreground" /> : <ChevronDown className="h-5 w-5 ml-auto text-muted-foreground" />}
              </button>
              {historyOpen && <div className="space-y-2">{history.map((b) => histExpanded.has(b.id) ? (
                <div key={b.id} className="space-y-1.5">
                  <div className="flex justify-end">
                    <Button size="sm" variant="ghost" className="h-7 gap-1.5 text-muted-foreground" onClick={() => setHistExpanded((s) => { const n = new Set(s); n.delete(b.id); return n; })} data-testid={`agenda-history-collapse-${b.id}`}><ChevronUp className="h-4 w-4" /> {tx("Inklappen")}</Button>
                  </div>
                  {bookingCard(b)}
                </div>
              ) : (
                <button key={b.id} type="button" onClick={() => setHistExpanded((s) => new Set(s).add(b.id))} className="w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border border-border bg-card/60 text-sm hover:bg-accent/40 transition-colors text-left" data-testid={`agenda-history-row-${b.id}`}>
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="font-medium shrink-0">{BOOKING_TYPES[b.type]?.label || b.type}</span>
                    <span className="text-muted-foreground truncate">· {bookingTitle(b) || houseName(b.house_id)}</span>
                    {ownBadge(b, `agenda-history-eigen-${b.id}`)}
                  </span>
                  <span className="text-muted-foreground shrink-0 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {formatDate(b.start)}<span className="hidden sm:inline">&nbsp;{fmt(b.start).slice(11)}</span></span>
                </button>
              ))}</div>}
            </section>
          )}
        </div>
      ))}

      {filteredGoogleEvents.length > 0 && (
        <div className="mt-10" data-testid="google-events-section">
          <div className="flex items-center gap-2 mb-4">
            <CalendarDays className="h-5 w-5 text-secondary" />
            <h2 className="font-display font-semibold text-xl">{tx("Uit je Google Agenda")}</h2>
            <span className="text-xs text-muted-foreground">({filteredGoogleEvents.length})</span>
          </div>
          <div className="space-y-3">
            {filteredGoogleEvents.map((e, i) => (
              <div key={`${e.id}-${i}`} className="bg-card rounded-xl border border-dashed border-secondary/40 p-5 shadow-sm" data-testid={`gevent-${e.id}`}>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-secondary/15 text-secondary">{tx("Google")}</span>
                      <h3 className="font-display font-semibold">{e.title}</h3>
                      {e.calendar && <span className="text-xs text-muted-foreground">· {e.calendar}</span>}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2 flex-wrap">
                      <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {e.all_day ? formatDate(e.start) : `${fmt(e.start)}${e.end ? ` – ${fmt(e.end)}` : ""}`}</span>
                      {e.location && <span>· {e.location}</span>}
                    </div>
                  </div>
                  {e.description && (
                    <button onClick={() => setExpanded((p) => ({ ...p, [e.id]: !p[e.id] }))} className="inline-flex items-center gap-1.5 text-sm text-secondary hover:text-secondary/80" data-testid={`gevent-desc-toggle-${e.id}`}>
                      {tx("Omschrijving")} {expanded[e.id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  )}
                  {e.html_link && (
                    <a href={e.html_link} target="_blank" rel="noreferrer" className="text-secondary hover:text-secondary/80" data-testid={`gevent-link-${e.id}`}>
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
                {e.description && expanded[e.id] && (
                  <div className="mt-3 pt-3 border-t border-border text-sm text-muted-foreground whitespace-pre-line" data-testid={`gevent-desc-${e.id}`}>
                    {e.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {makeResFor && <MakeReservationDialog seed={makeResFor} bookings={bookings} onClose={() => setMakeResFor(null)} onDone={() => { setMakeResFor(null); load(); loadTasks(); }} />}
      <Dialog open={!!detailBooking} onOpenChange={(o) => !o && setDetailBooking(null)}>
        <DialogContent data-testid="booking-detail-dialog">
          {detailBooking && (() => {
            const t = BOOKING_TYPES[detailBooking.type] || BOOKING_TYPES.checkin;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${t.color}`}>{t.label}</span>
                    {bookingTitle(detailBooking)}
                    {ownBadge(detailBooking, "booking-detail-eigen")}
                  </DialogTitle>
                  <DialogDescription className="sr-only">{tx("Details van de afspraak")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-2.5 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground"><Clock className="h-4 w-4 shrink-0" /> {fmt(detailBooking.start)}{detailBooking.end ? ` – ${fmt(detailBooking.end)}` : ""}</div>
                  {isTeam && detailBooking.type === "checkin" && detailBooking.departure && (
                    <div className="flex items-center gap-2 text-muted-foreground" data-testid="booking-detail-counter-checkout"><LogOut className="h-4 w-4 shrink-0" /> {tx("Uitcheck:")} {formatDate(detailBooking.departure)}{detailBooking.departure_at?.includes("T") ? ` om ${detailBooking.departure_at.split("T")[1].slice(0, 5)}` : ""}</div>
                  )}
                  {isTeam && detailBooking.type === "checkout" && detailBooking.arrival && (
                    <div className="flex items-center gap-2 text-muted-foreground" data-testid="booking-detail-counter-checkin"><Key className="h-4 w-4 shrink-0" /> {tx("Incheck:")} {formatDate(detailBooking.arrival)}</div>
                  )}
                  {detailBooking.party_composition && (
                    <div className="flex items-start gap-2 text-muted-foreground" data-testid="booking-detail-party"><Users className="h-4 w-4 shrink-0 mt-0.5" /> <span className="whitespace-pre-line">{detailBooking.party_composition}</span></div>
                  )}
                  {detailBooking.res_notes && (
                    <div className="rounded-lg bg-muted/50 p-3 whitespace-pre-line text-foreground/90 italic" data-testid="booking-detail-res-notes">"{detailBooking.res_notes}"</div>
                  )}
                  <IntakeSummary booking={detailBooking} />
                </div>
                {isTeam && !(detailBooking.reservation_id && isOwnStay(detailBooking)) && (
                  <DialogFooter className="flex-col sm:flex-col items-stretch gap-2">
                    {detailBooking.reservation_id ? (
                      <>
                        <Button onClick={() => { navigate(`/app/chat?res=${detailBooking.reservation_id}`); setDetailBooking(null); }} className="bg-primary hover:bg-primary/90 gap-2 w-full" data-testid="booking-open-chat-btn">
                          <MessageCircle className="h-4 w-4" /> {tx("Open chat met deze gast")}
                        </Button>
                        <p className="text-xs text-muted-foreground text-center">{tx("Incheck en uitcheck van dezelfde gast delen één gesprek.")}</p>
                      </>
                    ) : (
                      <>
                        {user.role === "admin" && (
                          <Button variant="outline" onClick={() => { setMakeResFor(detailBooking); setDetailBooking(null); }} className="gap-2 w-full" data-testid="booking-make-reservation-btn">
                            <CalendarPlus className="h-4 w-4" /> {tx("Maak boeking")}
                          </Button>
                        )}
                        {user.role === "admin" && !linkedTask(detailBooking) && ["checkin", "checkout", "cleaning", "maintenance", "own_before", "own_after"].includes(detailBooking.type) && (
                          <Button variant="outline" onClick={() => addChecklist(detailBooking)} className="gap-2 w-full" data-testid="booking-add-checklist-btn">
                            <ListChecks className="h-4 w-4" /> {tx("Checklist toevoegen")}
                          </Button>
                        )}
                        <p className="text-xs text-muted-foreground text-center" data-testid="booking-no-chat">{tx("Geen gastenchat: deze afspraak is niet aan een gastreservering gekoppeld.")}</p>
                      </>
                    )}
                  </DialogFooter>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!commentBooking} onOpenChange={(o) => !o && setCommentBooking(null)}>
        <DialogContent className="sm:max-w-xl" data-testid="booking-comments-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><MessageSquare className="h-5 w-5 text-primary" /> {tx("Opmerkingen ·")} {commentBooking?.title}</DialogTitle><DialogDescription className="sr-only">{tx("Opmerkingen bij deze afspraak")}</DialogDescription></DialogHeader>
          {commentBooking && (isTeam && ["checkin", "checkout"].includes(commentBooking.type) ? (
            <Tabs value={commentTab} onValueChange={setCommentTab}>
              <TabsList className="w-full"><TabsTrigger value="comments" className="flex-1" data-testid="comments-tab">{tx("Opmerkingen")}</TabsTrigger><TabsTrigger value="report" className="flex-1" data-testid="report-tab">{tx("Rapportage")}</TabsTrigger></TabsList>
              <TabsContent value="comments"><CommentThread entityType="booking" entityId={commentBooking.id} onChange={refreshNotif} /></TabsContent>
              <TabsContent value="report"><ReportPanel booking={commentBooking} onChange={() => canManage && loadPendingReports()} /></TabsContent>
            </Tabs>
          ) : <CommentThread entityType="booking" entityId={commentBooking.id} onChange={refreshNotif} />)}
        </DialogContent>
      </Dialog>

      <ReportSettingsDialog open={reportSettingsOpen} onOpenChange={setReportSettingsOpen} />
      {canManage && <BookingTimeDialog booking={timeEdit} onOpenChange={(o) => !o && setTimeEdit(null)} onSaved={(nb) => { setBookings((p) => (p || []).map((x) => (x.id === nb.id ? { ...x, start: nb.start, end: nb.end } : x))); load(); loadTasks(); }} />}
      {canManage && <GoogleImportDialog open={importOpen} onOpenChange={setImportOpen} houses={houses} staff={staff} me={user} onDone={() => { load(); loadGoogleEvents(); loadCalList(); }} />}
      <LinenDialog open={!!linenFor} onOpenChange={(o) => !o && setLinenFor(null)} houseId={linenFor?.houseId} houseName={linenFor?.houseName} reservationId={linenFor?.reservationId} onSaved={() => { if (!isFieldStaff) load(); loadTasks(); }} />
    </div>
  );
}
